/**
 * "Scrolled" — stand in the header state you cannot otherwise hold (LG-4).
 *
 * 72 of the 330 corpus headers that carry a logo restyle it on scroll: they
 * shrink its height, swap a light mark for a dark one, translate the whole bar,
 * or cross-fade two logos. Until this control that second design was reachable
 * only by scrolling past it and could not be held — so a user sized their logo
 * against the state they could see and met the other one on the shipped site.
 * The editor was hiding half the design.
 *
 * ── Why this is NOT a fourth viewport preset ──────────────────────────────
 * The obvious home is beside Desktop / Tablet / Mobile, and it would have been a
 * silent data bug. `VIEWPORTS` (`ui/viewport-controls.js`) is not switcher-local:
 * `set()` calls `_setActiveViewportName()`, which is what
 * `getResponsiveOverrideViewport()` returns, which is the bucket EVERY section
 * override is written into. `RESPONSIVE_VIEWPORTS` is a frozen three-element list
 * (`editing/section-utils.js`) consumed by `core/save-load.js`,
 * `section-properties/override-state.js` and eight guards in
 * `apply-section-override.js` — so a fourth entry named `scrolled` would make
 * every override written while it was active fall back to `null` with no error.
 *
 * It is also wrong semantically: scroll state is ORTHOGONAL to size. A header can
 * be mobile AND scrolled. A radio group cannot express that; a toggle can.
 *
 * ── Why the pin lives in the iframe ───────────────────────────────────────
 * `body.scroll` is written by a scroll listener the parser injects into every
 * template nav, so the parent cannot simply add the class — the next scroll event
 * would remove it. `set-scroll-state` sets a flag the section-JS facade reads and
 * suppresses removals against, which is the same shape as `set-comment-mode` and
 * `set-preview-mode`: a one-way parent→iframe body-class toggle.
 */

import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { createButton } from '../components.js';
import { icon } from '../icons.js';
import { bridge } from '../../editing/bridge.js';
import { hasScrolledLogoState } from '../../shared/logo-state-discovery.js';

function activeHeaderSupportsScrolledLogo(state = store.getState()) {
  const header = state?.sharedSections?.navigation;
  if (!header) return false;
  const doc = new DOMParser().parseFromString(`<div>${header.html || ''}</div>`, 'text/html');
  const logo = doc.querySelector(
    '.logo-slot, .cs-logo, .logo, .navbar-brand, .brand, .footer-logo'
  );
  if (!logo) return false;
  const mediaClasses = new Set();
  for (const media of doc.querySelectorAll(
    '.logo-slot img, .logo-slot svg, .logo-slot picture, .cs-logo img, .logo img'
  )) {
    for (const name of media.classList) mediaClasses.add(name);
  }
  return hasScrolledLogoState(header.css || '', mediaClasses);
}

export class ScrollStateToggle {
  /** @param {HTMLElement} container — the row-2 view slot */
  constructor(container) {
    this._pinned = false;
    this._offs = [];
    this._btn = createButton({
      variant: 'bare',
      className: 'pb-cb-icon pb-scroll-state-toggle',
      id: 'pb-scroll-state-toggle',
      icon: icon('scrollState', { size: 'sm' }),
      ariaLabel: 'Scrolled header state',
      tooltip: 'Show the header as it looks once the page is scrolled',
      onClick: () => this.set(!this._pinned)
    });
    this._btn.setAttribute('aria-pressed', 'false');
    container.appendChild(this._btn);
    this._refreshAvailability();

    // A full render rebuilds the iframe document, which drops the pin with it.
    // The comment controller re-sends its mode for the same reason; without this
    // the state silently un-pins on the next edit.
    this._offs.push(
      eventBus.on('iframe:bridge-ready', () => {
        this._refreshAvailability();
        if (this._pinned) this._send();
      })
    );
    this._offs.push(store.subscribe(() => this._refreshAvailability()));
    // BC-10: the phone's View sheet reaches this control through a command,
    // because row 2's right anchor (where this button lives) is not on a phone.
    this._offs.push(eventBus.on('toolbar:toggle-scroll-state', () => this.set(!this._pinned)));
  }

  get pinned() {
    return this._pinned;
  }

  set(pinned) {
    const next = Boolean(pinned);
    if (next && this._btn?.disabled) return;
    if (next === this._pinned) return;
    this._pinned = next;
    this._btn.setAttribute('aria-pressed', String(next));
    this._btn.classList.toggle('pb-active', next);
    this._send();
    eventBus.emit('viewport:scroll-state-changed', { pinned: next });
  }

  _send() {
    bridge.send('set-scroll-state', { pinned: this._pinned });
  }

  _refreshAvailability() {
    if (!this._btn) return;
    const available = activeHeaderSupportsScrolledLogo();
    const reason = 'This header does not restyle its logo when scrolled';
    this._btn.disabled = !available;
    this._btn.title = available ? 'Show the header as it looks once the page is scrolled' : reason;
    this._btn.setAttribute(
      'aria-label',
      available ? 'Scrolled header state' : `Scrolled header state — ${reason}`
    );
    if (!available && this._pinned) this.set(false);
  }

  destroy() {
    for (const off of this._offs) off?.();
    this._offs = [];
    this._btn?.remove();
    this._btn = null;
  }
}
